<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { Button, Checkbox, Dialog, InputNumber, InputText, Message, Popover, Select, Textarea, useConfirm } from 'primevue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useProductStore } from 'shared/products/stores/productStore';
import { ProductType } from 'shared/products/domain/productType';
import { usePackageTypeStore } from 'shared/packages/stores/packageTypeStore';
import { usePackagingStore } from 'shared/packages/stores/packagingStore';
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import { useRefreshPackageTypeStore } from '@/packages/commands/refreshPackageTypeStore';
import { useRefreshPackagingStore } from '@/packages/commands/refreshPackagingStore';
import { useMappingProfileStore } from 'shared/products/mapping/stores/mappingProfileStore';
import { useCreateMappingProfile } from '../api/createMappingProfile';
import { useUpdateMappingProfile } from '../api/updateMappingProfile';
import { buildMappingProfileFormValues } from './copyMappingProfile';
import PanelGridView from '@/common/panelGrid/PanelGridView.vue';
import PanelFieldGrid from '@/common/panelGrid/PanelFieldGrid.vue';
import { usePanelGridDialog } from '@/common/panelGrid/usePanelGridDialog';
import {
  mappingProfileDialogGridConfig,
  mappingProfileDialogStorageKey,
  type MappingProfilePanelId,
} from './mappingProfileDialogGridLayout';

defineExpose({ show });

const emit = defineEmits<{
  saved: [id: string];
}>();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const productStore = useProductStore();
const packageTypeStore = usePackageTypeStore();
const packagingStore = usePackagingStore();
const mappingProfileStore = useMappingProfileStore();

const refreshProductStore = useRefereshProductStore();
const refreshPackageTypeStore = useRefreshPackageTypeStore();
const refreshPackagingStore = useRefreshPackagingStore();

const createMappingProfile = useCreateMappingProfile();
const updateMappingProfile = useUpdateMappingProfile();

const visible = ref(false);
const loading = ref(false);
const mappingProfileId = ref<string | null>(null);

const name = ref('');
const description = ref('');
const priority = ref<number>(100);
const sourceCode = ref('');
const minWidth = ref<number | null>(null);
const maxWidth = ref<number | null>(null);
const minHeight = ref<number | null>(null);
const maxHeight = ref<number | null>(null);
const minLength = ref<number | null>(null);
const maxLength = ref<number | null>(null);
const minMoisture = ref<number | null>(null);
const maxMoisture = ref<number | null>(null);
const targetProductId = ref<string | null>(null);
const targetPackageTypeId = ref<string | null>(null);
const targetPackagingId = ref<string | null>(null);

const title = computed(() => (mappingProfileId.value ? 'Edit mapping profile' : 'Add mapping profile'));

const productOptions = computed(() => productStore.sortedByName.filter((item) => item.type === ProductType.Dimension).map((item) => ({ label: item.name, value: item.id })));
const packageTypeOptions = computed(() => packageTypeStore.sortedByName.map((item) => ({ label: item.name, value: item.id })));
const packagingOptions = computed(() => packagingStore.sortedByName.map((item) => ({ label: item.name, value: item.id })));

onMounted(() => {
  refreshProductStore.initialize();
  refreshPackageTypeStore.initialize();
  refreshPackagingStore.initialize();
});

const config = mappingProfileDialogGridConfig;

const {
  layoutEditMode,
  gridLayout,
  gridGroups,
  editingSheetId,
  editingSheetName,
  draggedSheetId,
  hoverSheetId,
  selectTabsheet,
  resetLayout,
  moveGroupToNewSheet,
  moveGroupToSheet,
  startRenameTabsheet,
  commitRenameTabsheet,
  onTabsheetDrop,
  panelFieldSettings,
  isFieldVisible,
  setFieldVisible,
  setLabelWidth,
  activePickerPanel,
  dialogStyle,
  dialogContentStyle,
  startResize,
} = usePanelGridDialog<MappingProfilePanelId>({
  config,
  layoutStorageKey: mappingProfileDialogStorageKey,
  editModeStorageKey: 'mapping-profile-dialog-layout-edit-mode',
  visible,
});

const confirm = useConfirm();

function confirmResetLayout() {
  confirm.require({
    header: translations.value.generic.resetLayoutConfirmHeader,
    message: translations.value.generic.resetLayoutConfirmMessage,
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: translations.value.generic.abort, severity: 'secondary', outlined: true },
    acceptProps: { label: translations.value.generic.reset, severity: 'warn' },
    accept: () => resetLayout(),
  });
}

const panelFieldDefs = computed<Record<MappingProfilePanelId, { key: string; label: string }[]>>(() => ({
  core: [
    { key: 'name', label: translations.value.generic.name },
    { key: 'description', label: translations.value.generic.description },
    { key: 'priority', label: 'Priority' },
  ],
  input: [
    { key: 'sourceCode', label: 'Source code' },
    { key: 'width', label: 'Width min / max' },
    { key: 'height', label: 'Height min / max' },
    { key: 'length', label: 'Length min / max' },
    { key: 'moisture', label: 'Moisture min / max' },
  ],
  output: [
    { key: 'targetProduct', label: 'Target product' },
    { key: 'targetPackageType', label: 'Target package type' },
    { key: 'targetPackaging', label: 'Target packaging' },
  ],
}));

const panelTitles: Record<MappingProfilePanelId, string> = {
  core: 'Core',
  input: 'Input',
  output: 'Output',
};

const fieldPicker = useTemplateRef<InstanceType<typeof Popover>>('fieldPicker');

function toggleFieldPicker(panelId: MappingProfilePanelId, event: Event) {
  activePickerPanel.value = panelId;
  fieldPicker.value?.toggle(event);
}

function reset() {
  mappingProfileId.value = null;
  name.value = '';
  description.value = '';
  priority.value = 100;
  sourceCode.value = '';
  minWidth.value = null;
  maxWidth.value = null;
  minHeight.value = null;
  maxHeight.value = null;
  minLength.value = null;
  maxLength.value = null;
  minMoisture.value = null;
  maxMoisture.value = null;
  targetProductId.value = null;
  targetPackageTypeId.value = null;
  targetPackagingId.value = null;
}

function show(id?: string, mode: 'edit' | 'copy' = 'edit') {
  reset();

  if (!id) {
    visible.value = true;
    return;
  }

  const item = mappingProfileStore.getById(id);
  if (!item) {
    return;
  }

  const copyName = mode === 'copy' ? translations.value.generic.copyOfName : (n: string) => n;
  const values = buildMappingProfileFormValues(item, copyName);

  // Copy mode leaves mappingProfileId null so save() creates a new profile.
  mappingProfileId.value = mode === 'copy' ? null : item.id;
  name.value = values.name;
  description.value = values.description;
  priority.value = values.priority;
  sourceCode.value = values.sourceCode;
  minWidth.value = values.minWidth;
  maxWidth.value = values.maxWidth;
  minHeight.value = values.minHeight;
  maxHeight.value = values.maxHeight;
  minLength.value = values.minLength;
  maxLength.value = values.maxLength;
  minMoisture.value = values.minMoisture;
  maxMoisture.value = values.maxMoisture;
  targetProductId.value = values.targetProductId;
  targetPackageTypeId.value = values.targetPackageTypeId;
  targetPackagingId.value = values.targetPackagingId;
  visible.value = true;
}

function asPayload() {
  return {
    name: name.value.trim(),
    description: description.value.trim(),
    priority: priority.value ?? 100,
    sourceCode: sourceCode.value.trim() || null,
    minWidth: minWidth.value,
    maxWidth: maxWidth.value,
    minHeight: minHeight.value,
    maxHeight: maxHeight.value,
    minLength: minLength.value,
    maxLength: maxLength.value,
    minMoisture: minMoisture.value,
    maxMoisture: maxMoisture.value,
    targetProductId: targetProductId.value,
    targetPackageTypeId: targetPackageTypeId.value,
    targetPackagingId: targetPackagingId.value,
  };
}

const canSave = computed(() => !!name.value.trim());

async function save() {
  if (!canSave.value) {
    return;
  }

  loading.value = true;
  try {
    const payload = asPayload();
    const response = mappingProfileId.value
      ? await updateMappingProfile.execute({ id: mappingProfileId.value, entity: payload })
      : await createMappingProfile.execute({ entity: payload });

    if (!response.ok) {
      return;
    }

    visible.value = false;
    emit('saved', response.mappingProfile.id);
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <Dialog v-model:visible="visible" modal :header="title" :style="dialogStyle" :contentStyle="dialogContentStyle" :draggable="true" class="mapping-profile-edit-dialog">
    <div class="dialog-body">
      <div class="dialog-top-controls">
        <Button
          v-if="layoutEditMode"
          text
          rounded
          icon="pi pi-undo"
          class="customization-toggle-button"
          :aria-label="translations.generic.reset"
          @click="confirmResetLayout"
        ></Button>
        <span class="layout-mode-label">Layout</span>
        <Button
          text
          rounded
          :icon="layoutEditMode ? 'pi pi-times' : 'pi pi-sliders-h'"
          class="customization-toggle-button"
          aria-label="Toggle layout options"
          @click="layoutEditMode = !layoutEditMode"
        ></Button>
      </div>

      <Message v-if="!canSave" severity="warn" size="small" variant="simple" class="validation-message">
        {{ translations.generic.name }} is required.
      </Message>

      <div v-if="gridLayout.tabsheets.length > 1" class="tabsheet-bar">
        <div
          v-for="sheet in gridLayout.tabsheets"
          :key="sheet.id"
          class="tabsheet"
          :class="{ 'is-active': sheet.id === gridLayout.activeTabsheetId, 'is-drop-target': hoverSheetId === sheet.id }"
          :data-sheet-id="sheet.id"
          :draggable="layoutEditMode && editingSheetId !== sheet.id"
          @click="selectTabsheet(sheet.id)"
          @dragstart="draggedSheetId = sheet.id"
          @dragover.prevent
          @drop="onTabsheetDrop(sheet.id)"
        >
          <InputText
            v-if="editingSheetId === sheet.id"
            v-model="editingSheetName"
            class="tabsheet-input"
            @click.stop
            @keyup.enter="commitRenameTabsheet"
            @blur="commitRenameTabsheet"
          ></InputText>
          <template v-else>
            <span class="tabsheet-name">{{ sheet.name }}</span>
            <i
              v-if="layoutEditMode"
              class="pi pi-pencil tabsheet-edit"
              title="Rename tabsheet"
              @click.stop="startRenameTabsheet(sheet)"
            ></i>
          </template>
        </div>
      </div>

      <div class="dialog-workspace">
        <PanelGridView
          v-model="gridGroups"
          :config="config"
          :panel-titles="panelTitles"
          :layout-edit-mode="layoutEditMode"
          @move-to-new-sheet="moveGroupToNewSheet"
          @move-to-sheet="moveGroupToSheet"
          @sheet-hover="hoverSheetId = $event"
        >
          <template #actions-core>
            <Button v-if="layoutEditMode" text rounded icon="pi pi-list" class="field-picker-button" aria-label="Toggle fields" @click="toggleFieldPicker('core', $event)"></Button>
          </template>
          <template #panel-core>
            <PanelFieldGrid
              :fields="panelFieldDefs.core"
              :hidden="panelFieldSettings('core').hidden"
              :label-width="panelFieldSettings('core').labelWidth"
              :layout-edit-mode="layoutEditMode"
              :min-label-width="config.label.min"
              :max-label-width="config.label.max"
              @update:label-width="setLabelWidth('core', $event)"
            >
              <template #name><InputText v-model="name"></InputText></template>
              <template #description><Textarea v-model="description" rows="3" auto-resize></Textarea></template>
              <template #priority><InputNumber v-model="priority" :min="0" showButtons></InputNumber></template>
            </PanelFieldGrid>
          </template>

          <template #actions-input>
            <Button v-if="layoutEditMode" text rounded icon="pi pi-list" class="field-picker-button" aria-label="Toggle fields" @click="toggleFieldPicker('input', $event)"></Button>
          </template>
          <template #panel-input>
            <PanelFieldGrid
              :fields="panelFieldDefs.input"
              :hidden="panelFieldSettings('input').hidden"
              :label-width="panelFieldSettings('input').labelWidth"
              :layout-edit-mode="layoutEditMode"
              :min-label-width="config.label.min"
              :max-label-width="config.label.max"
              @update:label-width="setLabelWidth('input', $event)"
            >
              <template #sourceCode><InputText v-model="sourceCode"></InputText></template>
              <template #width>
                <div class="range-grid">
                  <InputNumber v-model="minWidth" :min="0" showButtons></InputNumber>
                  <InputNumber v-model="maxWidth" :min="0" showButtons></InputNumber>
                </div>
              </template>
              <template #height>
                <div class="range-grid">
                  <InputNumber v-model="minHeight" :min="0" showButtons></InputNumber>
                  <InputNumber v-model="maxHeight" :min="0" showButtons></InputNumber>
                </div>
              </template>
              <template #length>
                <div class="range-grid">
                  <InputNumber v-model="minLength" :min="0" showButtons></InputNumber>
                  <InputNumber v-model="maxLength" :min="0" showButtons></InputNumber>
                </div>
              </template>
              <template #moisture>
                <div class="range-grid">
                  <InputNumber v-model="minMoisture" :min="0" :max="100" :minFractionDigits="0" :maxFractionDigits="2" showButtons></InputNumber>
                  <InputNumber v-model="maxMoisture" :min="0" :max="100" :minFractionDigits="0" :maxFractionDigits="2" showButtons></InputNumber>
                </div>
              </template>
            </PanelFieldGrid>
          </template>

          <template #actions-output>
            <Button v-if="layoutEditMode" text rounded icon="pi pi-list" class="field-picker-button" aria-label="Toggle fields" @click="toggleFieldPicker('output', $event)"></Button>
          </template>
          <template #panel-output>
            <PanelFieldGrid
              :fields="panelFieldDefs.output"
              :hidden="panelFieldSettings('output').hidden"
              :label-width="panelFieldSettings('output').labelWidth"
              :layout-edit-mode="layoutEditMode"
              :min-label-width="config.label.min"
              :max-label-width="config.label.max"
              @update:label-width="setLabelWidth('output', $event)"
            >
              <template #targetProduct>
                <Select v-model="targetProductId" :options="productOptions" optionLabel="label" optionValue="value" showClear filter></Select>
              </template>
              <template #targetPackageType>
                <Select v-model="targetPackageTypeId" :options="packageTypeOptions" optionLabel="label" optionValue="value" showClear filter></Select>
              </template>
              <template #targetPackaging>
                <Select v-model="targetPackagingId" :options="packagingOptions" optionLabel="label" optionValue="value" showClear filter></Select>
              </template>
            </PanelFieldGrid>
          </template>
        </PanelGridView>

        <Popover ref="fieldPicker">
          <div class="field-picker">
            <div v-for="field in panelFieldDefs[activePickerPanel]" :key="field.key" class="field-picker-row">
              <Checkbox
                :inputId="`fp-${activePickerPanel}-${field.key}`"
                :modelValue="isFieldVisible(activePickerPanel, field.key)"
                binary
                @update:modelValue="setFieldVisible(activePickerPanel, field.key, $event)"
              ></Checkbox>
              <label :for="`fp-${activePickerPanel}-${field.key}`">{{ field.label }}</label>
            </div>
          </div>
        </Popover>
      </div>
    </div>

    <template #footer>
      <Button :label="translations.generic.cancel" text @click="visible = false"></Button>
      <Button :label="translations.generic.save" :loading="loading" :disabled="!canSave" @click="save"></Button>
      <div v-if="layoutEditMode" class="resize-handle" @mousedown.prevent="startResize"></div>
    </template>
  </Dialog>
</template>

<style scoped>
.dialog-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
  min-height: 0;
}

.dialog-top-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-bottom: 0.25rem;
}

.layout-mode-label {
  color: var(--p-text-muted-color);
  font-size: 0.82rem;
}

.customization-toggle-button {
  flex: 0 0 auto;
}

.tabsheet-bar {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: wrap;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

.tabsheet {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.6rem;
  border: 1px solid var(--p-content-border-color);
  border-bottom: none;
  border-radius: 0.4rem 0.4rem 0 0;
  background: color-mix(in srgb, var(--p-content-background) 92%, var(--p-primary-color) 8%);
  color: var(--p-text-color);
  font-size: 0.82rem;
  cursor: pointer;
  user-select: none;
}

.tabsheet.is-active {
  background: color-mix(in srgb, var(--p-content-background) 74%, var(--p-primary-color) 26%);
  font-weight: 600;
}

.tabsheet.is-drop-target {
  border-color: var(--p-primary-color);
  box-shadow: inset 0 0 0 1px var(--p-primary-color);
}

.tabsheet-edit {
  font-size: 0.72rem;
  opacity: 0.6;
}

.tabsheet-edit:hover {
  opacity: 1;
  color: var(--p-primary-color);
}

.tabsheet-input {
  width: 8rem;
  height: 1.5rem;
  padding: 0 0.35rem;
  font-size: 0.82rem;
}

.dialog-workspace {
  flex: 1;
  min-height: 0;
}

.range-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}

.validation-message {
  margin: 0;
}

.field-picker {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 12rem;
  max-height: 20rem;
  overflow: auto;
}

.field-picker-button.p-button {
  width: 1.6rem;
  height: 1.6rem;
  padding: 0;
}

.field-picker-button :deep(.p-button-icon) {
  font-size: 0.82rem;
}

.field-picker-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.field-picker-row label {
  cursor: pointer;
}

.resize-handle {
  position: absolute;
  right: 0.35rem;
  bottom: 0.35rem;
  width: 14px;
  height: 14px;
  cursor: nwse-resize;
  border-right: 2px solid var(--p-text-muted-color);
  border-bottom: 2px solid var(--p-text-muted-color);
}
</style>
